import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { onAuthStateChanged, signOut as firebaseSignOut } from "firebase/auth";
import { storage } from "firebase-admin";
import { UserData } from "../data/data";
import { ensureUserFields, getUserData } from "../firebase/authservice";
import { auth } from "../firebase/firebaseConfig";

interface UnifiedUserContextData {
  isUserLoggedIn: boolean;
  user: UserData | null;

  updateUser: (userData: UserData) => void;
  signOut: () => void;
  resetUserContext: () => void;
  isUserDataLoading: boolean;
}

const UnifiedUserContext = createContext<UnifiedUserContextData | undefined>(
  undefined
);

export const UnifiedUserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [authMethods, setAuthMethods] = useState<string[]>([]);
  const [isUserDataLoading, setIsUserDataLoading] = useState(true);
  // const [presentAlert] = useIonAlert();
  // const Navigation = useIonRouter();

  useEffect(() => {
    let loadingTimeout: NodeJS.Timeout;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsUserDataLoading(true);

      if (!firebaseUser) {
        setIsUserLoggedIn(false);
        setUser(null);
        setIsUserDataLoading(false);
        return;
      }

      try {
        if (
          firebaseUser.providerData[0].providerId === "google.com" ||
          firebaseUser.providerData[0].providerId === "apple.com"
        ) {
          await ensureUserFields(firebaseUser);
        }

        const userData = await getUserData();

        setUser(userData);
        setIsUserLoggedIn(true);
      } catch (error) {
        console.error("Error fetching user data", error);
        setIsUserLoggedIn(false);
        setUser(null);
      } finally {
        clearTimeout(loadingTimeout);
        setIsUserDataLoading(false);
      }
    });

    return () => {
      unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    resetUserContext();
  }, []);

  const updateUser = useCallback((userData: UserData) => {
    setUser((prevUser) => ({ ...prevUser, ...userData }));
  }, []);

  const resetUserContext = useCallback(() => {
    setUser(null);
    setIsUserLoggedIn(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      isUserLoggedIn,
      user,
      authMethods,
      updateUser,
      signOut,
      resetUserContext,
      isUserDataLoading,
    }),
    [
      isUserDataLoading,
      isUserLoggedIn,
      authMethods,
      signOut,
      user,
      updateUser,
      resetUserContext,
    ]
  );

  return (
    <UnifiedUserContext.Provider value={contextValue}>
      {children}
    </UnifiedUserContext.Provider>
  );
};

export const useUnifiedUser = () => {
  const context = useContext(UnifiedUserContext);
  if (context === undefined) {
    throw new Error("useUnifiedUser must be used within a UnifiedUserProvider");
  }
  return context;
};
